import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import { useRightPanel } from './rightPanelContext'

interface UseModuleNotificationsParams {
  moduleKey: string
  scope?: string | number | null
  items: string[]
  loaded?: boolean
  visible: boolean
}

type SeenEntry = [string, string[]]

export const MAX_REMEMBERED_SCOPES = 50

const storageKey = (userId: unknown, moduleKey: string) => `hop-sidebar-seen-${userId ?? 'anon'}-${moduleKey}`

const isSeenEntry = (entry: unknown): entry is SeenEntry =>
  Array.isArray(entry) && typeof entry[0] === 'string' && Array.isArray(entry[1])

const readEntries = (key: string): SeenEntry[] => {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(key) ?? '[]')
    return Array.isArray(parsed) ? parsed.filter(isSeenEntry) : []
  } catch {
    return []
  }
}

const readSeen = (key: string, scope: string): string[] | null =>
  readEntries(key).find(([entryScope]) => entryScope === scope)?.[1] ?? null

const recordSeen = (key: string, scope: string, items: string[]): string[] => {
  const entries = readEntries(key)
  const previous = entries.find(([entryScope]) => entryScope === scope)?.[1] ?? []
  const others = entries.filter(([entryScope]) => entryScope !== scope)
  const seen = [...new Set([...previous, ...items])]
  try {
    window.localStorage.setItem(key, JSON.stringify([...others, [scope, seen]].slice(-MAX_REMEMBERED_SCOPES)))
  } catch {
    // Private browsing or full storage: the badge only resets next time.
  }
  return seen
}

const useModuleNotifications = ({ moduleKey, scope, items, loaded = true, visible }: UseModuleNotificationsParams) => {
  const { userId } = useSelector(selectAuthData)
  const { setNotification } = useRightPanel()
  const ready = loaded && scope !== undefined && scope !== null
  const key = storageKey(userId, moduleKey)
  const scopeKey = String(scope)
  const seenId = `${key}|${scopeKey}`
  const signature = items.join('\u0000')
  const [seen, setSeen] = useState<{ id: string; items: string[] } | null>(null)

  useEffect(() => {
    if (!ready) return
    const stored = readSeen(key, scopeKey)
    setSeen({ id: seenId, items: stored === null || visible ? recordSeen(key, scopeKey, items) : stored })
  }, [ready, key, scopeKey, seenId, signature, visible])

  const count =
    ready && !visible && seen?.id === seenId ? items.filter(item => !seen.items.includes(item)).length : 0

  useEffect(() => {
    setNotification(moduleKey, count)
  }, [moduleKey, count, setNotification])

  useEffect(() => () => setNotification(moduleKey, 0), [moduleKey, setNotification])

  return { count }
}

export default useModuleNotifications
